import {
  GRANTABLE_PERMISSIONS,
  GRANTABLE_PERMISSION_NAMES,
  HOLOGRAPHIC_COLORS,
  MAX_LEVEL,
  PERMISSION_ADMINISTRATOR,
  PERMISSION_MANAGE_ROLES,
  cardPartsOf,
  carriedClosures,
  carriesOf,
  requiresOf,
  isCardReward,
  isCoreReward,
  permissionBits,
  unitedChannelAccess,
} from "@colossus/contracts";
import type {
  GrantablePermission,
  LevelingMetric,
  ManagedRole,
  MemberCardPart,
  PersonalRolePart,
  Reward,
  RewardGrant,
  RewardStacking,
  RoleAnchor,
  RoleAnchors,
  RoleIcon,
  RoleStyle,
  RewardTrigger,
  Rewards,
} from "@colossus/contracts";
import { m } from "./paraglide/messages.js";
import { plural } from "./format.ts";
import { beyondPerks } from "./discord-role-permissions.ts";
import { guessAnchor, heldPerksOf } from "./leveling-onboard.ts";
import type { HeldPermission } from "./discord-role-permissions.ts";
import type { Entity, RoleEntity } from "./entities.ts";
import { SOLID } from "./leveling-role-style.ts";
import type { Locale, Message } from "./locale.ts";
import { ALL_TIME, windowWords } from "./window.ts";

export interface TriggerDraft {
  kind: RewardTrigger["kind"];
  level: string;
  inClimb: boolean;
  climb: string;
  prestige: string;
  top: string;
  minXp: string;
  metric: LevelingMetric;
  min: string;
  span: string;
  keep: boolean;
  apps: string[];
  days: string;
  invites: string;
  roleId: string | null;
  open: TriggerOpen;
  by: LevelingMetric | null;
}

export type TriggerOpen = "any" | "has" | null;

export interface AlsoDraft {
  readonly key: number;
  trigger: TriggerDraft;
}

export const spanOf = (windowDays: number | null): string =>
  windowDays === null ? ALL_TIME : String(windowDays);

export const windowOf = (span: string): number | null =>
  span === ALL_TIME ? null : numberOf(span);

export interface TargetAccess {
  readonly allow: readonly GrantablePermission[];
  readonly deny: readonly GrantablePermission[];
}

export interface ChannelAccessDraft {
  readonly key: number;
  targets: string[];
  allow: GrantablePermission[];
  deny: GrantablePermission[];
}

export interface ManagedRoleDraft {
  mode: ManagedRole["mode"];
  roleId: string | null;
  name: string;
  color: string | null;
  style: RoleStyle;
  hoist: boolean;
  mentionable: boolean;
  permissions: GrantablePermission[];
  channels: ChannelAccessDraft[];
  icon: RoleIcon | null;
  owned: boolean;
}

export type RoleLook = Pick<ManagedRoleDraft, "name" | "color" | "style" | "hoist" | "mentionable">;

export const discordLook = (role: RoleEntity): RoleLook | null => {
  if (role.hoist === null || role.mentionable === null) return null;
  const style: RoleStyle =
    role.tertiaryColor !== null
      ? { kind: "holographic" }
      : role.secondaryColor !== null && role.color !== null
        ? { kind: "gradient", secondary: role.secondaryColor }
        : SOLID;
  return {
    name: role.name,
    color: role.color,
    style,
    hoist: role.hoist,
    mentionable: role.mentionable,
  };
};

export const adoptRole = (
  grant: GrantDraft,
  roleId: string,
  fact: RoleEntity | undefined,
): void => {
  const look = fact === undefined ? null : discordLook(fact);
  grant.kind = "managed_role";
  grant.role.mode = "adopt";
  grant.role.roleId = roleId;
  grant.role.owned = false;
  grant.role.permissions = fact === undefined ? [] : [...heldPerksOf(fact)];
  if (look !== null) Object.assign(grant.role, look, { owned: true });
};

export const lookToOwn = (
  grant: GrantDraft,
  lookOf: (roleId: string) => RoleLook | null,
): RoleLook | null => {
  const { role } = grant;
  if (grant.kind !== "managed_role" || role.mode !== "adopt" || role.owned) return null;
  return role.roleId === null ? null : lookOf(role.roleId);
};

interface GrantDraft {
  kind: RewardGrant["kind"] | "none";
  roleId: string | null;
  role: ManagedRoleDraft;
}

export interface RewardDraft {
  readonly key: number;
  readonly id: string;
  trigger: TriggerDraft;
  requires: AlsoDraft[];
  carries: string[];
  grant: GrantDraft;
  cardEditing: MemberCardPart[];
  levelUpImageId: string | null;
  personalRole: PersonalRolePart[];
}

export interface RewardsDraft {
  drift: Rewards["drift"];
  stacking: RewardStacking;
  anchors: RoleAnchors;
  restoreOnRejoin: boolean;
  items: RewardDraft[];
}

let nextKey = 0;

export const draftKey = (): number => {
  nextKey += 1;
  return nextKey;
};

export const numberOf = (text: string): number => (text.trim() === "" ? Number.NaN : Number(text));

export const wholeOf = (text: string, least = 1): number | null => {
  const trimmed = text.trim();
  const value = Number(trimmed);
  return trimmed !== "" && Number.isInteger(value) && value >= least ? value : null;
};

export const pastCeiling = (trigger: TriggerDraft, ceiling: number | null): boolean => {
  if (trigger.kind !== "level" || ceiling === null) return false;
  const level = wholeOf(trigger.level);
  return level !== null && level > ceiling;
};

const TRIGGER_START: Omit<TriggerDraft, "kind"> = {
  level: "5",
  inClimb: false,
  climb: "1",
  prestige: "1",
  top: "3",
  minXp: "500",
  metric: "messages",
  min: "500",
  span: "30",
  keep: false,
  apps: [],
  days: "30",
  invites: "5",
  roleId: null,
  open: null,
  by: null,
};

const triggerDraft = (trigger: RewardTrigger): TriggerDraft => {
  switch (trigger.kind) {
    case "level":
      return {
        ...TRIGGER_START,
        kind: "level",
        level: String(trigger.level),
        ...(trigger.prestige === undefined
          ? {}
          : { inClimb: true, climb: String(trigger.prestige) }),
      };
    case "prestige":
      return { ...TRIGGER_START, kind: "prestige", prestige: String(trigger.prestige) };
    case "placing":
      return {
        ...TRIGGER_START,
        kind: "placing",
        by: trigger.metric === undefined ? null : trigger.metric,
        span: spanOf(trigger.windowDays),
        top: String(trigger.top),
      };
    case "activity":
      return {
        ...TRIGGER_START,
        kind: "activity",
        span: spanOf(trigger.windowDays),
        minXp: String(trigger.minXp),
      };
    case "stat":
      return {
        ...TRIGGER_START,
        kind: "stat",
        metric: trigger.metric,
        min: String(trigger.min),
        span: spanOf(trigger.windowDays),
        keep: trigger.keep === true,
        apps: trigger.apps === undefined ? [] : [...trigger.apps],
      };
    case "tenure":
      return { ...TRIGGER_START, kind: "tenure", days: String(trigger.days) };
    case "invites":
      return { ...TRIGGER_START, kind: "invites", invites: String(trigger.min) };
    case "role":
      return { ...TRIGGER_START, kind: "role", roleId: trigger.roleId };
  }
};

export const newAlso = (kind: RewardTrigger["kind"] = "role"): AlsoDraft => ({
  key: draftKey(),
  trigger: { ...TRIGGER_START, kind },
});

const samePermissions = (
  left: readonly GrantablePermission[],
  right: readonly GrantablePermission[],
): boolean => left.length === right.length && left.every((name) => right.includes(name));

const accessDrafts = (channels: ManagedRole["channels"]): ChannelAccessDraft[] => {
  const groups: ChannelAccessDraft[] = [];
  for (const entry of channels) {
    const deny = entry.deny === undefined ? [] : entry.deny;
    const last = groups.at(-1);
    const same =
      last !== undefined &&
      samePermissions(last.allow, entry.allow) &&
      samePermissions(last.deny, deny);
    if (same) last.targets.push(entry.targetId);
    else {
      groups.push({
        key: draftKey(),
        targets: [entry.targetId],
        allow: [...entry.allow],
        deny: [...deny],
      });
    }
  }
  return groups;
};

const EMPTY_ROLE: ManagedRoleDraft = {
  mode: "create",
  roleId: null,
  name: "",
  color: null,
  style: SOLID,
  hoist: false,
  mentionable: false,
  permissions: [],
  channels: [],
  icon: null,
  owned: false,
};

const managedDraft = (role: ManagedRole): ManagedRoleDraft => ({
  mode: role.mode,
  roleId: role.roleId,
  name: role.appearance === null ? "" : role.appearance.name,
  color: role.appearance === null ? null : role.appearance.color,
  style:
    role.appearance === null || role.appearance.style === undefined ? SOLID : role.appearance.style,
  hoist: role.appearance === null ? false : role.appearance.hoist,
  mentionable: role.appearance === null ? false : role.appearance.mentionable,
  permissions: [...role.permissions],
  channels: accessDrafts(role.channels),
  icon: role.icon === undefined ? null : role.icon,
  owned: role.appearance !== null,
});

const grantDraft = (grant: RewardGrant | undefined): GrantDraft => {
  const blank = { roleId: null, role: { ...EMPTY_ROLE } };
  if (grant === undefined) return { ...blank, kind: "none" };
  switch (grant.kind) {
    case "role":
      return { ...blank, kind: "role", roleId: grant.roleId };
    case "managed_role":
      return { ...blank, kind: "managed_role", role: managedDraft(grant.role) };
    case "access":
      return {
        ...blank,
        kind: "access",
        role: {
          ...EMPTY_ROLE,
          permissions: [...grant.permissions],
          channels: accessDrafts(grant.channels),
        },
      };
  }
};

export const rewardDraft = (reward: Reward): RewardDraft => ({
  key: draftKey(),
  id: reward.id,
  trigger: triggerDraft(reward.trigger),
  requires: requiresOf(reward).map((trigger) => ({
    key: draftKey(),
    trigger: triggerDraft(trigger),
  })),
  carries: [...carriesOf(reward)],
  grant: grantDraft(reward.grant),
  cardEditing: [...cardPartsOf(reward)],
  levelUpImageId: reward.levelUpImageId === undefined ? null : reward.levelUpImageId,
  personalRole: reward.personalRole === undefined ? [] : [...reward.personalRole],
});

export const rewardsDraft = (rewards: Rewards): RewardsDraft => {
  const items = rewards.items.map(rewardDraft);
  return {
    drift: rewards.drift,
    stacking: rewards.stacking,
    anchors: { ...rewards.anchors },
    restoreOnRejoin: rewards.restoreOnRejoin,
    items,
  };
};

export const newAccessReward = (roleId: string, existing: readonly RewardDraft[]): RewardDraft => {
  const made = newReward("role", existing);
  made.trigger.roleId = roleId;
  made.grant = { kind: "access", roleId: null, role: { ...EMPTY_ROLE } };
  return made;
};

export const addToLadder = (rewards: RewardsDraft, added: readonly RewardDraft[]): void => {
  rewards.items.push(...added);
};

const newRewardId = (): string => `reward-${crypto.randomUUID().slice(0, 8)}`;

export const newReward = (
  kind: RewardTrigger["kind"],
  existing: readonly RewardDraft[],
): RewardDraft => {
  const highest = (field: "level" | "prestige"): number => {
    const found = existing
      .filter((item) => item.trigger.kind === field)
      .map((item) => numberOf(item.trigger[field]))
      .filter(Number.isFinite);
    return found.length === 0 ? 0 : Math.max(...found);
  };
  return {
    key: draftKey(),
    id: newRewardId(),
    trigger: {
      ...TRIGGER_START,
      kind,
      level: String(highest("level") + 10),
      prestige: String(highest("prestige") + 1),
    },
    requires: [],
    carries: [],
    grant: { kind: "managed_role", roleId: null, role: { ...EMPTY_ROLE } },
    cardEditing: [],
    levelUpImageId: null,
    personalRole: [],
  };
};

export const holdsMoreThanMembers = (
  role: { readonly permissions: string | null },
  everyone: string | null,
): boolean => {
  const beyond = beyondPerks(role.permissions, everyone);
  return beyond !== null && beyond.length > 0;
};

export const mayTakeOver = (
  role: { readonly permissions: string | null },
  everyone: string | null,
  mayStrip: boolean,
): boolean => mayStrip || !holdsMoreThanMembers(role, everyone);

export const copyReward = (item: RewardDraft): RewardDraft => ({
  key: draftKey(),
  id: newRewardId(),
  trigger: { ...item.trigger },
  requires: item.requires.map((condition) => ({
    key: draftKey(),
    trigger: { ...condition.trigger },
  })),
  carries: [...item.carries],
  grant: {
    kind: item.grant.kind,
    roleId: null,
    role: {
      ...item.grant.role,
      roleId: item.grant.role.mode === "create" ? item.grant.role.roleId : null,
      permissions: [...item.grant.role.permissions],
      channels: item.grant.role.channels.map((access) => ({
        key: draftKey(),
        targets: [...access.targets],
        allow: [...access.allow],
        deny: [...access.deny],
      })),
    },
  },
  cardEditing: [...item.cardEditing],
  levelUpImageId: item.levelUpImageId,
  personalRole: [...item.personalRole],
});

export const rewardFromRole = (
  roleId: string,
  level: number,
  fact: RoleEntity | undefined,
): RewardDraft => {
  const made = rewardDraft({ id: newRewardId(), trigger: { kind: "level", level } });
  adoptRole(made.grant, roleId, fact);
  return made;
};

export const newAccess = (): ChannelAccessDraft => ({
  key: draftKey(),
  targets: [],
  allow: ["view_channel"],
  deny: [],
});

const triggerOf = (draft: TriggerDraft): RewardTrigger => {
  switch (draft.kind) {
    case "level":
      return {
        kind: "level",
        level: numberOf(draft.level),
        ...(draft.inClimb ? { prestige: numberOf(draft.climb) } : {}),
      };
    case "prestige":
      return { kind: "prestige", prestige: numberOf(draft.prestige) };
    case "placing":
      return {
        kind: "placing",
        windowDays: windowOf(draft.span),
        top: numberOf(draft.top),
        ...(draft.by === null ? {} : { metric: draft.by }),
      };
    case "activity":
      return {
        kind: "activity",
        windowDays: windowOf(draft.span),
        minXp: numberOf(draft.minXp),
      };
    case "stat":
      return {
        kind: "stat",
        metric: draft.metric,
        windowDays: windowOf(draft.span),
        min: numberOf(draft.min),
        ...(draft.keep ? { keep: true as const } : {}),
        ...(draft.metric === "app_minutes" && draft.apps.length > 0
          ? { apps: [...draft.apps] }
          : {}),
      };
    case "tenure":
      return { kind: "tenure", days: numberOf(draft.days) };
    case "invites":
      return { kind: "invites", min: numberOf(draft.invites) };
    case "role":
      return { kind: "role", roleId: draft.roleId === null ? "" : draft.roleId };
  }
};

const inContractOrder = (names: Iterable<GrantablePermission>): GrantablePermission[] => {
  const held = new Set(names);
  return GRANTABLE_PERMISSION_NAMES.filter((name) => held.has(name));
};

const channelAccessOf = (groups: readonly ChannelAccessDraft[]): ManagedRole["channels"] =>
  unitedChannelAccess(
    groups.flatMap((group) =>
      group.targets.map((targetId) => ({
        targetId,
        allow: group.allow,
        ...(group.deny.length === 0 ? {} : { deny: group.deny }),
      })),
    ),
  );

export const grantedTargets = (
  groups: readonly ChannelAccessDraft[],
): ReadonlyMap<string, TargetAccess> =>
  new Map(
    channelAccessOf(groups).map((entry) => [
      entry.targetId,
      { allow: entry.allow, deny: entry.deny === undefined ? [] : entry.deny },
    ]),
  );

const managedOf = (draft: ManagedRoleDraft): ManagedRole => ({
  mode: draft.mode,
  roleId: draft.mode === "adopt" ? draft.roleId : null,
  appearance:
    draft.mode === "create" || draft.owned
      ? {
          name: draft.name,
          color: draft.color,
          ...(draft.style.kind === "solid" ? {} : { style: draft.style }),
          hoist: draft.hoist,
          mentionable: draft.mentionable,
        }
      : null,
  permissions: inContractOrder(draft.permissions),
  channels: channelAccessOf(draft.channels),
  ...(draft.icon === null ? {} : { icon: draft.icon }),
});

const grantOf = (draft: GrantDraft): RewardGrant | undefined => {
  switch (draft.kind) {
    case "role":
      return { kind: "role", roleId: draft.roleId === null ? "" : draft.roleId };
    case "managed_role":
      return { kind: "managed_role", role: managedOf(draft.role) };
    case "access":
      return {
        kind: "access",
        permissions: inContractOrder(draft.role.permissions),
        channels: channelAccessOf(draft.role.channels),
      };
    case "none":
      return undefined;
  }
};

const givesAccess = (item: RewardDraft): boolean =>
  item.grant.kind === "managed_role" || item.grant.kind === "access";

export const carriedRewards = (
  items: readonly RewardDraft[],
  reward: RewardDraft,
): readonly RewardDraft[] => {
  const ids = new Set(items.map((item) => item.id));
  const closures = carriedClosures(
    items.map((item) => ({
      id: item.id,
      trigger: triggerOf(item.trigger),
      carries: item.carries.filter((id) => ids.has(id)),
    })),
  );
  const reached = new Set((closures.get(reward.id) ?? []).map((entry) => entry.id));
  return items.filter((item) => reached.has(item.id) && givesAccess(item));
};

const sourceName = (item: RewardDraft, roles: readonly Entity[]): string => {
  if (
    item.grant.kind === "access" &&
    item.trigger.kind === "role" &&
    item.trigger.roleId !== null
  ) {
    const { roleId } = item.trigger;
    const found = roles.find((role) => role.id === roleId);
    return found === undefined ? roleId : found.name;
  }
  const named = grantName(item.grant, roles);
  return named === null ? m.leveling_phrase_new_role() : named;
};

export const carriedPermissions = (
  carried: readonly RewardDraft[],
  roles: readonly Entity[],
): ReadonlyMap<GrantablePermission, string> => {
  const from = new Map<GrantablePermission, string>();
  for (const item of carried) {
    if (!givesAccess(item)) continue;
    const name = sourceName(item, roles);
    for (const permission of item.grant.role.permissions) {
      if (!from.has(permission)) from.set(permission, name);
    }
  }
  return from;
};

export const carriedChannels = (
  carried: readonly RewardDraft[],
): ReadonlyMap<string, TargetAccess> =>
  grantedTargets(carried.flatMap((item) => (givesAccess(item) ? item.grant.role.channels : [])));

export const rewardOf = (draft: RewardDraft): Reward => {
  const grant = grantOf(draft.grant);
  return {
    id: draft.id,
    trigger: triggerOf(draft.trigger),
    ...(draft.requires.length === 0
      ? {}
      : {
          requires: draft.requires.map((condition) =>
            triggerOf({ ...condition.trigger, keep: false }),
          ),
        }),
    ...(draft.carries.length === 0 ? {} : { carries: [...draft.carries] }),
    ...(grant === undefined ? {} : { grant }),
    ...(draft.cardEditing.length === 0 ? {} : { cardEditing: [...draft.cardEditing] }),
    ...(draft.levelUpImageId === null ? {} : { levelUpImageId: draft.levelUpImageId }),
    ...(draft.personalRole.length === 0 ? {} : { personalRole: [...draft.personalRole] }),
  };
};

export const rewardsOf = (draft: RewardsDraft): Rewards => {
  const ids = new Set(draft.items.map((item) => item.id));
  return {
    drift: draft.drift,
    stacking: draft.stacking,
    anchors: { ...draft.anchors },
    restoreOnRejoin: draft.restoreOnRejoin,
    items: draft.items.map((item) =>
      rewardOf({
        ...item,
        carries: [...new Set(item.carries)].filter((id) => id !== item.id && ids.has(id)),
      }),
    ),
  };
};

const format = (text: string, locale: string): string => {
  const value = numberOf(text);
  return Number.isFinite(value) ? new Intl.NumberFormat(locale).format(value) : text;
};

export const METRIC_UNIT: Readonly<Record<LevelingMetric, Message>> = {
  messages: m.perks_unit_messages,
  active_minutes: m.perks_unit_active_minutes,
  words: m.perks_unit_words,
  chars: m.perks_unit_chars,
  attachments: m.perks_unit_attachments,
  stickers: m.perks_unit_stickers,
  links: m.perks_unit_links,
  gif_messages: m.perks_unit_gif_messages,
  reactions_given: m.perks_unit_reactions_given,
  reactions_received: m.perks_unit_reactions_received,
  mentions_made: m.perks_unit_mentions_made,
  mentions_received: m.perks_unit_mentions_received,
  replies: m.perks_unit_replies,
  replies_received: m.perks_unit_replies_received,
  voice_minutes: m.perks_unit_voice_minutes,
  streaming_minutes: m.perks_unit_streaming_minutes,
  video_minutes: m.perks_unit_video_minutes,
  command_invocations: m.perks_unit_command_invocations,
  emoji_sent: m.perks_unit_emoji_sent,
  app_minutes: m.perks_unit_app_minutes,
};

export const triggerPhrase = (
  trigger: TriggerDraft,
  locale: string,
  roleName: (roleId: string) => string | null = () => null,
): string => {
  switch (trigger.kind) {
    case "level":
      return trigger.inClimb
        ? m.leveling_ladder_level_in_prestige({
            level: format(trigger.level, locale),
            prestige: format(trigger.climb, locale),
          })
        : m.leveling_ladder_level({ level: format(trigger.level, locale) });
    case "prestige":
      return m.leveling_ladder_prestige({ prestige: format(trigger.prestige, locale) });
    case "placing": {
      const top = format(trigger.top, locale);
      const days = Number(trigger.span);
      const phrase =
        trigger.span === ALL_TIME
          ? m.leveling_reward_top_all({ top })
          : plural(locale, days, { one: m.leveling_reward_top_one, other: m.leveling_reward_top })({
              top,
              days: format(trigger.span, locale),
              n: days,
            });
      return trigger.by === null
        ? phrase
        : m.perks_trigger_ranked({ phrase, metric: METRIC_UNIT[trigger.by]() });
    }
    case "activity": {
      const xp = format(trigger.minXp, locale);
      if (trigger.span === ALL_TIME) return m.leveling_reward_activity_all({ xp });
      const days = Number(trigger.span);
      return plural(locale, days, {
        one: m.leveling_reward_activity_one,
        other: m.leveling_reward_activity,
      })({ xp, days: format(trigger.span, locale), n: days });
    }
    case "stat": {
      const slots = { min: format(trigger.min, locale), metric: METRIC_UNIT[trigger.metric]() };
      if (trigger.span === ALL_TIME) return m.perks_trigger_stat_all(slots);
      const days = Number(trigger.span);
      return plural(locale, days, { one: m.perks_trigger_stat_one, other: m.perks_trigger_stat })({
        ...slots,
        days: format(trigger.span, locale),
        n: days,
      });
    }
    case "tenure":
      return plural(locale, Number(trigger.days), {
        one: m.perks_trigger_tenure_one,
        other: m.perks_trigger_tenure,
      })({ days: format(trigger.days, locale), n: Number(trigger.days) });
    case "invites":
      return plural(locale, Number(trigger.invites), {
        one: m.perks_trigger_invites_one,
        other: m.perks_trigger_invites,
      })({ count: format(trigger.invites, locale), n: Number(trigger.invites) });
    case "role": {
      const name = trigger.roleId === null ? null : roleName(trigger.roleId);
      return name === null ? m.perks_trigger_role_any() : m.perks_trigger_role({ role: name });
    }
  }
};

export interface TriggerHead {
  readonly when: string;
  readonly qualifier: string | undefined;
}

export const triggerHead = (
  trigger: TriggerDraft,
  locale: Locale,
  roleName: (roleId: string) => string | null = () => null,
): TriggerHead => {
  const span = windowWords(windowOf(trigger.span), locale);
  switch (trigger.kind) {
    case "level":
      return {
        when: m.leveling_ladder_level({ level: format(trigger.level, locale) }),
        qualifier: trigger.inClimb
          ? m.leveling_ladder_prestige({ prestige: format(trigger.climb, locale) })
          : undefined,
      };
    case "placing":
      return {
        when:
          trigger.by === null
            ? m.perks_head_top({ top: format(trigger.top, locale) })
            : m.perks_trigger_ranked({
                phrase: m.perks_head_top({ top: format(trigger.top, locale) }),
                metric: METRIC_UNIT[trigger.by](),
              }),
        qualifier: span,
      };
    case "activity":
      return { when: m.perks_head_xp({ xp: format(trigger.minXp, locale) }), qualifier: span };
    case "stat":
      return {
        when: m.perks_head_stat({
          min: format(trigger.min, locale),
          metric: METRIC_UNIT[trigger.metric](),
        }),
        qualifier: span,
      };
    case "prestige":
    case "tenure":
    case "invites":
    case "role":
      return { when: triggerPhrase(trigger, locale, roleName), qualifier: undefined };
  }
};

export const rewardTriggerPhrase = (
  trigger: RewardTrigger,
  locale: string,
  roleName?: (roleId: string) => string | null,
): string => triggerPhrase(triggerDraft(trigger), locale, roleName);

const roleName = (roleId: string | null, roles: readonly Entity[]): string | null =>
  roleId === null ? null : (roles.find((role) => role.id === roleId)?.name ?? roleId);

export const grantName = (grant: GrantDraft, roles: readonly Entity[]): string | null => {
  switch (grant.kind) {
    case "role": {
      const name = roleName(grant.roleId, roles);
      return name === null ? null : m.leveling_ladder_role_name({ name });
    }
    case "managed_role": {
      const name =
        grant.role.mode === "create" || grant.role.owned
          ? grant.role.name.trim() === ""
            ? null
            : grant.role.name.trim()
          : roleName(grant.role.roleId, roles);
      return name === null ? null : m.leveling_ladder_role_name({ name });
    }
    case "access":
    case "none":
      return null;
  }
};

export type RewardFeature = "advanced" | "member-cards" | "reward-cards";

export const missingFeature = (
  reward: Reward,
  plan: {
    readonly advanced: boolean;
    readonly memberCards: boolean;
    readonly rewardCards: boolean;
  },
): RewardFeature | null => {
  if (reward.grant !== undefined && !isCoreReward(reward) && !plan.advanced) return "advanced";
  if (isCardReward(reward) && !plan.memberCards) return "member-cards";
  if (reward.levelUpImageId !== undefined && !plan.rewardCards) return "reward-cards";
  return null;
};

export const namedRole = (grant: GrantDraft): string | null => {
  if (grant.kind === "role") return grant.roleId;
  return grant.kind === "managed_role" && grant.role.mode === "adopt" ? grant.role.roleId : null;
};

export type RoleCaution =
  | { readonly kind: "integration" }
  | { readonly kind: "above" }
  | {
      readonly kind: "elevated" | "stripped";
      readonly permissions: readonly HeldPermission[];
    };

export const roleCaution = (
  grant: GrantDraft,
  roles: readonly RoleEntity[],
  everyone: string | null,
  reach: ColossusReach | null,
): RoleCaution | null => {
  const roleId = namedRole(grant);
  const role = roleId === null ? undefined : roles.find((entry) => entry.id === roleId);
  if (role === undefined) return null;
  if (role.managed === true) return { kind: "integration" };
  if (reach !== null && aboveColossus(role, reach)) return { kind: "above" };
  const kind = grant.kind === "role" ? "elevated" : "stripped";
  const permissions = beyondPerks(role.permissions, kind === "elevated" ? everyone : null);
  return permissions === null || permissions.length === 0 ? null : { kind, permissions };
};

export interface ColossusReach {
  readonly permissions: bigint;
  readonly top: number;
}

export const colossusReach = (
  roles: readonly RoleEntity[],
  everyone: string | null,
): ColossusReach | null => {
  const held = roles.filter((role) => role.heldByColossus === true);
  if (held.length === 0) return null;
  const permissions = held.reduce(
    (bits, role) => bits | (role.permissions === null ? 0n : BigInt(role.permissions)),
    everyone === null ? 0n : BigInt(everyone),
  );
  const top = Math.max(...held.map((role) => (role.position === null ? 0 : role.position)));
  return { permissions, top };
};

const colossusLacks = (reach: ColossusReach, wanted: bigint): bigint =>
  (reach.permissions & PERMISSION_ADMINISTRATOR) !== 0n ? 0n : wanted & ~reach.permissions;

export const colossusManagesRoles = (reach: ColossusReach): boolean =>
  colossusLacks(reach, PERMISSION_MANAGE_ROLES) === 0n;

export const aboveColossus = (role: RoleEntity, reach: ColossusReach): boolean =>
  role.position !== null && role.position >= reach.top;

export const anchorAbove = (
  anchorId: string | null,
  roles: readonly RoleEntity[],
  reach: ColossusReach | null,
): boolean => {
  const role = anchorId === null ? undefined : roles.find((entry) => entry.id === anchorId);
  return role !== undefined && reach !== null && aboveColossus(role, reach);
};

export const unheldGrants = (
  grant: GrantDraft,
  reach: ColossusReach,
): readonly GrantablePermission[] => {
  if (grant.kind !== "managed_role") return [];
  const lacking = colossusLacks(reach, permissionBits(grant.role.permissions));
  return GRANTABLE_PERMISSION_NAMES.filter(
    (name) => (lacking & GRANTABLE_PERMISSIONS[name]) !== 0n,
  );
};

export const ladderUnheld = (
  items: readonly RewardDraft[],
  reach: ColossusReach,
): readonly GrantablePermission[] => {
  const unheld = new Set(items.flatMap((item) => unheldGrants(item.grant, reach)));
  return GRANTABLE_PERMISSION_NAMES.filter((name) => unheld.has(name));
};

const TRIGGER_RANK: Readonly<Record<RewardTrigger["kind"], number>> = {
  level: 0,
  prestige: 1,
  placing: 2,
  activity: 3,
  stat: 4,
  tenure: 5,
  invites: 6,
  role: 7,
};

const CLIMB_SPAN = MAX_LEVEL + 1;

const scaleOf = (trigger: TriggerDraft): number => {
  switch (trigger.kind) {
    case "level":
      return (trigger.inClimb ? numberOf(trigger.climb) * CLIMB_SPAN : 0) + numberOf(trigger.level);
    case "prestige":
      return numberOf(trigger.prestige);
    case "placing":
      return (
        (trigger.span === ALL_TIME ? 0 : numberOf(trigger.span)) * 1_000 + numberOf(trigger.top)
      );
    case "activity":
      return numberOf(trigger.minXp);
    case "stat":
      return numberOf(trigger.min);
    case "tenure":
      return numberOf(trigger.days);
    case "invites":
      return numberOf(trigger.invites);
    case "role":
      return 0;
  }
};

export const rungOf = (trigger: TriggerDraft): number => {
  const at = scaleOf(trigger);
  return Number.isFinite(at) ? at : Number.POSITIVE_INFINITY;
};

export const rewardLadder = (
  items: readonly RewardDraft[],
  held: { readonly key: number; readonly at: number } | null,
): readonly RewardDraft[] => {
  const rank = (item: RewardDraft): readonly [number, number] => [
    TRIGGER_RANK[item.trigger.kind],
    held !== null && item.key === held.key ? held.at : rungOf(item.trigger),
  ];
  return items
    .map((item, index) => ({ item, index, rank: rank(item) }))
    .sort(
      (left, right) =>
        left.rank[0] - right.rank[0] || left.rank[1] - right.rank[1] || left.index - right.index,
    )
    .map((entry) => entry.item);
};

export interface ManagedRoleView {
  readonly rewardId: string;
  readonly roleId: string | null;
  readonly status: string;
  readonly drift: readonly string[];
  readonly detail: string;
}

export const rewardRole = (
  item: RewardDraft,
  managed: readonly ManagedRoleView[],
): string | null => {
  if (item.grant.kind === "none") return null;
  if (item.grant.kind === "role") return item.grant.roleId;
  if (item.grant.role.roleId !== null) return item.grant.role.roleId;
  const view = managed.find((entry) => entry.rewardId === item.id);
  return view === undefined ? null : view.roleId;
};

export const createsRole = (item: RewardDraft): boolean =>
  item.grant.kind === "managed_role" && item.grant.role.mode === "create";

export const proposedAnchor = (
  anchor: RoleAnchor,
  roles: readonly RoleEntity[],
  items: readonly RewardDraft[],
  managed: readonly ManagedRoleView[],
): string | null =>
  guessAnchor(
    roles.map((role) => ({ roleId: role.id, name: role.name, position: role.position })),
    anchor,
    anchor === "personal"
      ? []
      : items.flatMap((item) => {
          const roleId = rewardRole(item, managed);
          return roleId === null ? [] : [roleId];
        }),
  );

type StatusTone = "warning" | "critical";

interface RowStatus {
  readonly label: string;
  readonly tone: StatusTone;
}

const DRIFT_LABEL: Readonly<Record<string, Message>> = {
  name: m.leveling_drift_name,
  color: m.leveling_drift_color,
  hoist: m.leveling_drift_hoist,
  mentionable: m.leveling_drift_mentionable,
  permissions: m.leveling_drift_permissions,
  position: m.leveling_drift_position,
  channels: m.leveling_drift_channels,
  icon: m.leveling_drift_icon,
};

export const driftWords = (fields: readonly string[], locale: string): string =>
  new Intl.ListFormat(locale, { type: "conjunction" }).format(
    fields.map((field) => {
      const label = DRIFT_LABEL[field];
      return label === undefined ? field : label();
    }),
  );

export const carryNote = (
  roleId: string,
  roles: readonly RoleEntity[],
  reach: ColossusReach | null,
  states: readonly {
    readonly roleId: string;
    readonly status: string;
    readonly drift: readonly string[];
    readonly detail: string;
  }[],
  locale: string,
): RowStatus | null => {
  const found = roles.find((entry) => entry.id === roleId);
  const role = found === undefined ? roleId : found.name;
  const above =
    found !== undefined &&
    ((found.heldByColossus === true && found.managed === true) ||
      (reach !== null && aboveColossus(found, reach)));
  if (above) return { label: m.perks_carry_above({ role }), tone: "critical" };
  const state = states.find((entry) => entry.roleId === roleId);
  switch (state?.status) {
    case "failed":
      return { label: m.perks_carry_failed({ role, detail: state.detail }), tone: "critical" };
    case "deleted":
      return { label: m.perks_carry_deleted({ role }), tone: "critical" };
    case "drifted":
      return {
        label: m.perks_carry_drifted({ role, fields: driftWords(state.drift, locale) }),
        tone: "warning",
      };
  }
  return null;
};

const primaryOf = (look: RoleLook): string | null => {
  const color = look.style.kind === "holographic" ? HOLOGRAPHIC_COLORS.primary : look.color;
  return color === null ? null : color.toLowerCase();
};

export const wornPlain = (
  grant: GrantDraft,
  view: ManagedRoleView | undefined,
  roles: readonly RoleEntity[],
): boolean => {
  const { role: look } = grant;
  if (grant.kind !== "managed_role" || look.style.kind === "solid") return false;
  if (view === undefined || view.status !== "failed") return false;
  const roleId = look.roleId === null ? view.roleId : look.roleId;
  const role = roles.find((entry) => entry.id === roleId);
  const worn = role === undefined ? null : discordLook(role);
  return (
    worn !== null &&
    worn.style.kind === "solid" &&
    worn.name === look.name &&
    worn.hoist === look.hoist &&
    worn.mentionable === look.mentionable &&
    primaryOf(worn) === primaryOf(look)
  );
};

export const managedStatus = (view: ManagedRoleView, locale: string): RowStatus | null => {
  switch (view.status) {
    case "drifted":
      return {
        label: m.leveling_managed_drifted({ fields: driftWords(view.drift, locale) }),
        tone: "warning",
      };
    case "failed":
      return { label: m.leveling_managed_failed(), tone: "critical" };
    case "deleted":
      return { label: m.leveling_managed_deleted(), tone: "critical" };
    default:
      return null;
  }
};
